import type { ApiResponse } from '@shared/contracts/mockApi';
import type {
  LayoutAuditEvent,
  LayoutPatchOperation,
  LayoutVersionSnapshot,
} from '@shared/contracts/customisation';
import { endpoint, etagOf } from './transport';
import { ERROR_CODES, MockApiError } from './errors';
import { db } from './db';
import { nowIso } from '@/services/clock';
import { once } from '@/services/idempotency';
import { recordAudit } from '@/services/audit';
import {
  POLICY_VERSION,
  applyOperations,
  governedDefault,
  validateOperations,
} from '@/customisation/policyEngine';
import { isKnownWidget, type MobileWidgetId } from '@/customisation/widgetRegistry';
import type { EntitlementContext } from '@/services/entitlements';

/**
 * Layout persistence — the mock `/ui/*` contract.
 *
 * Architecture rules encoded here:
 *  - the service is the source of truth, never browser storage;
 *  - saves use optimistic concurrency (`version` + `etag`) and idempotency keys;
 *  - every change is versioned, audited and restorable.
 */

export type StoredView = {
  viewId: string;
  ownerId: string;
  companyId: string;
  order: MobileWidgetId[];
  version: number;
  etag: string;
  updatedAt: string;
};

const views = new Map<string, StoredView>();
const versions = new Map<string, LayoutVersionSnapshot[]>();
const layoutAudit: LayoutAuditEvent[] = [];
let auditSeq = 0;

function keyFor(ownerId: string, companyId: string): string {
  return `${companyId}:${ownerId}`;
}

export function resetLayouts(): void {
  views.clear();
  versions.clear();
  layoutAudit.length = 0;
  auditSeq = 0;
}

function makeView(ownerId: string, companyId: string, order: readonly MobileWidgetId[]): StoredView {
  const body = { ownerId, companyId, order };
  return {
    viewId: `view_${companyId}_${ownerId}`,
    ownerId,
    companyId,
    order: [...order],
    version: 1,
    etag: etagOf(body),
    updatedAt: nowIso(),
  };
}

function recordLayoutAudit(
  view: StoredView,
  actorId: string,
  action: LayoutAuditEvent['action'],
  operations: readonly LayoutPatchOperation[],
  aiProposalId?: string,
): void {
  auditSeq += 1;
  layoutAudit.push({
    eventId: `levt_${String(auditSeq).padStart(5, '0')}`,
    viewId: view.viewId,
    version: view.version,
    actorId,
    action,
    operationIds: operations.map((o) => o.operationId),
    policyId: 'mobile-home-policy',
    policyVersion: POLICY_VERSION,
    ...(aiProposalId ? { aiProposalId } : {}),
    createdAt: nowIso(),
  });
}

export function layoutAuditEvents(): readonly LayoutAuditEvent[] {
  return layoutAudit;
}

/** GET /ui/pages/:pageId/effective-view */
export function getEffectiveView(ctx: EntitlementContext): Promise<ApiResponse<StoredView>> {
  return endpoint(() => {
    const key = keyFor(ctx.user.id, ctx.company.id);
    let view = views.get(key);
    if (!view) {
      view = makeView(ctx.user.id, ctx.company.id, governedDefault(ctx));
      views.set(key, view);
    }
    // Entitlement can change between sessions: drop widgets the user has lost
    // rather than rendering something they may no longer see.
    const allowed = new Set(governedDefault(ctx));
    const filtered = view.order.filter((id) => isKnownWidget(id) && allowed.has(id));
    return { ...view, order: filtered };
  });
}

export type SaveLayoutInput = {
  readonly ctx: EntitlementContext;
  readonly operations: readonly LayoutPatchOperation[];
  readonly baseVersion: number;
  readonly baseEtag: string;
  readonly idempotencyKey: string;
  readonly aiProposalId?: string;
  readonly changeSummary: string;
};

/** PATCH /ui/views/:viewId — If-Match/etag required. */
export function saveLayout(input: SaveLayoutInput): Promise<ApiResponse<StoredView>> {
  const { ctx, operations, baseVersion, baseEtag, idempotencyKey } = input;
  return endpoint(
    () =>
      once(idempotencyKey, () => {
        const key = keyFor(ctx.user.id, ctx.company.id);
        const view = views.get(key);
        if (!view) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'No saved layout to update.');

        // Optimistic concurrency: another device may have published first.
        if (view.version !== baseVersion || view.etag !== baseEtag) {
          throw new MockApiError(
            ERROR_CODES.VERSION_CONFLICT,
            'Your home layout was changed on another device.',
          );
        }

        const validation = validateOperations(ctx, view.order, operations);
        if (!validation.valid) {
          throw new MockApiError(ERROR_CODES.POLICY_VIOLATION, validation.violations.join(' '));
        }

        // Snapshot the outgoing version first so rollback is always possible.
        const history = versions.get(view.viewId) ?? [];
        history.push({
          viewId: view.viewId,
          version: view.version,
          etag: view.etag,
          snapshot: {
            viewId: view.viewId,
            version: view.version,
            etag: view.etag,
            name: 'Mobile home',
            scope: 'personal',
            ownerId: view.ownerId,
            pageId: 'mobile-home',
            isDefault: false,
            status: 'published',
            widgets: view.order.map((id, index) => ({
              instanceId: id,
              widgetId: id,
              widgetVersion: 1,
              config: {},
              layout: { desktop: null, tablet: null, narrow: { x: 0, y: index, w: 4, h: 1 } },
            })),
            createdAt: view.updatedAt,
            updatedAt: view.updatedAt,
          },
          createdBy: ctx.user.id,
          createdAt: nowIso(),
          changeSummary: input.changeSummary,
        });
        versions.set(view.viewId, history);

        const nextOrder = applyOperations(view.order, operations);
        view.order = [...nextOrder];
        view.version += 1;
        view.etag = etagOf({ ownerId: view.ownerId, companyId: view.companyId, order: view.order });
        view.updatedAt = nowIso();

        recordLayoutAudit(
          view,
          ctx.user.id,
          input.aiProposalId ? 'ai-apply' : 'update',
          operations,
          input.aiProposalId,
        );
        recordAudit({
          companyId: ctx.company.id,
          programmeId: db().programme.id,
          actorName: ctx.user.name,
          actorType: 'customer',
          action: input.aiProposalId ? 'layout.ai-applied' : 'layout.updated',
          objectType: 'layout',
          objectId: view.viewId,
          customerVisible: true,
          summary: input.changeSummary,
          after: { version: view.version, order: view.order },
        });

        return Promise.resolve({ ...view });
      }),
    { mutating: true },
  );
}

/** POST /ui/views/:viewId/rollback */
export function rollbackLayout(
  ctx: EntitlementContext,
  toVersion: number,
): Promise<ApiResponse<StoredView>> {
  return endpoint(
    () => {
      const key = keyFor(ctx.user.id, ctx.company.id);
      const view = views.get(key);
      if (!view) throw new MockApiError(ERROR_CODES.NOT_FOUND, 'No saved layout.');
      const history = versions.get(view.viewId) ?? [];
      const snapshot = history.find((h) => h.version === toVersion);
      if (!snapshot) {
        throw new MockApiError(ERROR_CODES.NOT_FOUND, 'That version is no longer available.');
      }

      const restored = snapshot.snapshot.widgets
        .map((w) => w.widgetId)
        .filter((id): id is MobileWidgetId => isKnownWidget(id));

      view.order = [...restored];
      view.version += 1;
      view.etag = etagOf({ ownerId: view.ownerId, companyId: view.companyId, order: view.order });
      view.updatedAt = nowIso();

      recordLayoutAudit(view, ctx.user.id, 'restore', []);
      recordAudit({
        companyId: ctx.company.id,
        programmeId: db().programme.id,
        actorName: ctx.user.name,
        actorType: 'customer',
        action: 'layout.rolled-back',
        objectType: 'layout',
        objectId: view.viewId,
        customerVisible: true,
        summary: `Restored the home layout to version ${toVersion}`,
        after: { version: view.version, order: view.order },
      });

      return { ...view };
    },
    { mutating: true },
  );
}

/** Reset to the governed default for this user. */
export function resetLayoutToDefault(ctx: EntitlementContext): Promise<ApiResponse<StoredView>> {
  return endpoint(
    () => {
      const key = keyFor(ctx.user.id, ctx.company.id);
      const view = views.get(key) ?? makeView(ctx.user.id, ctx.company.id, governedDefault(ctx));
      view.order = [...governedDefault(ctx)];
      view.version += 1;
      view.etag = etagOf({ ownerId: view.ownerId, companyId: view.companyId, order: view.order });
      view.updatedAt = nowIso();
      views.set(key, view);

      recordLayoutAudit(view, ctx.user.id, 'update', []);
      recordAudit({
        companyId: ctx.company.id,
        programmeId: db().programme.id,
        actorName: ctx.user.name,
        actorType: 'customer',
        action: 'layout.reset',
        objectType: 'layout',
        objectId: view.viewId,
        customerVisible: true,
        summary: 'Reset the home layout to the default',
        after: { version: view.version, order: view.order },
      });

      return { ...view };
    },
    { mutating: true },
  );
}

export function layoutVersions(viewId: string): readonly LayoutVersionSnapshot[] {
  return versions.get(viewId) ?? [];
}

/**
 * Test/demo hook: simulate another device publishing a change, so the
 * version-conflict path can be exercised.
 */
export function simulateRemoteLayoutChange(ctx: EntitlementContext): void {
  const key = keyFor(ctx.user.id, ctx.company.id);
  const view = views.get(key);
  if (!view) return;
  view.version += 1;
  view.etag = etagOf({ remote: true, at: nowIso() });
  view.updatedAt = nowIso();
}

/**
 * Consume compatible priorities published from the web experience.
 *
 * TRACE:UADD-V5-SMEM-PERS-001-05 — a published web order is applied as a
 * *priority hint* over the mobile-safe set. Desktop-only widgets and desktop
 * geometry are discarded rather than forced onto a phone.
 */
export function applyWebPublishedPriorities(
  ctx: EntitlementContext,
  webOrder: readonly string[],
): Promise<ApiResponse<StoredView>> {
  return endpoint(
    () => {
      const key = keyFor(ctx.user.id, ctx.company.id);
      const view = views.get(key) ?? makeView(ctx.user.id, ctx.company.id, governedDefault(ctx));
      const allowed = new Set(governedDefault(ctx));

      const hinted = webOrder.filter(
        (id): id is MobileWidgetId => isKnownWidget(id) && allowed.has(id),
      );
      const remainder = [...allowed].filter((id) => !hinted.includes(id));

      view.order = [...applyOperations([], [])];
      view.order = [...hinted, ...remainder];
      // Mobile pinning always wins over an imported desktop order.
      view.order = [...applyOperations(view.order, [])];
      view.version += 1;
      view.etag = etagOf({ ownerId: view.ownerId, companyId: view.companyId, order: view.order });
      view.updatedAt = nowIso();
      views.set(key, view);

      recordLayoutAudit(view, ctx.user.id, 'update', []);
      return { ...view };
    },
    { mutating: true },
  );
}
